import { useLanguage, t, formatCount } from "../../../i18n";
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Container, Row, Col, Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import Header from "../../../components/layout/Header/Header";
import MobileOffcanvasNav from "../../../components/layout/MobileOffcanvasNav/MobileOffcanvasNav";
import ListingFilters from "../../../components/listing/ListingFilters/ListingFilters";
import ListingCard from "../../../components/listing/ListingCard/ListingCard";
import EmptyState from "../../../components/common/EmptyState/EmptyState";
import VerificationNoticeBanner from "../../../components/common/VerificationNoticeBanner/VerificationNoticeBanner";
import { useAuth } from "../../../features/auth/context/AuthContext";
import { useLocation } from "../../../features/location/context/LocationContext";
import getRoomListings from "../../../features/listing/services/getRoomListings";
import getFavouriteRoomListings from "../../../features/shortlist/services/getFavouriteRoomListings";
import getCities from "../../../features/profile/services/getCities";
import getNeighbourhoods from "../../../features/profile/services/getNeighbourhoods";
import getPropertyTypes from "../../../features/profile/services/getPropertyTypes";
import getCommunityPreferences from "../../../features/profile/services/getCommunityPreferences";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import ROUTES from "../../../config/routes";
import "./ListingsFeed.css";
import Logo from "../../../components/common/Logo/Logo";
import Footer from "../../../components/layout/Footer/Footer";
const LISTINGS_PER_LOAD = 6;
const SEARCH_DEBOUNCE_DELAY = 300;
const roomTypeOptions = ["Bedspace", "Shared Room", "Private Room", "Studio Room", "Partition Room"];
const genderOptions = ["Male Only", "Female Only"];
const furnishedOptions = ["Yes", "No"];
function normalizeUser(sourceUser = {}) {
  return {
    id: sourceUser?.user_id || sourceUser?.id || "",
    user_id: sourceUser?.user_id || sourceUser?.id || "",
    name: sourceUser?.name || sourceUser?.full_name || "BaytBuddy User",
    full_name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    email: sourceUser?.email || "",
    mobile: sourceUser?.mobile || "",
    profile_status: sourceUser?.profile_status || "",
    nafath_status: sourceUser?.nafath_status || ""
  };
}
function normalizeSearchText(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}
function toTitleCase(value) {
  return String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}
function mapGenderPreference(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male") return "Male Only";
  if (normalized === "female") return "Female Only";
  return value || "Not specified";
}
function mapFurnished(value) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const normalized = String(value || "").trim().toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return "Yes";
  if (["0", "false", "no"].includes(normalized)) return "No";
  return value ? String(value) : "No";
}
function getPrimaryImage(listing = {}) {
  if (listing?.primary_image) {
    return listing.primary_image;
  }
  if (Array.isArray(listing?.images) && listing.images.length > 0) {
    const primaryImage = listing.images.find(item => item?.is_primary) || listing.images[0];
    return primaryImage?.url || "";
  }
  return "";
}
function extractListingsArray(response) {
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}
function extractFavouriteIds(response) {
  const favouritesArray = Array.isArray(response?.data?.data?.data) ? response.data.data.data : Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
  return new Set(favouritesArray.map(item => {
    return item?.room_listing?.id || item?.id || item?.room_listing_id || item?.listing_id;
  }).filter(Boolean).map(value => String(value)));
}
function mapApiListingToCard(listing, favouriteIds = new Set()) {
  const listingId = listing?.id;
  const isFavouriteFromApi = Boolean(listing?.is_favourite || listing?.raw?.is_favourite);
  const isFavouriteFromFavouriteApi = favouriteIds.has(String(listingId));
  const isFavourite = isFavouriteFromApi || isFavouriteFromFavouriteApi;
  return {
    id: listingId,
    title: listing?.title || "Untitled Listing",
    image: getPrimaryImage(listing),
    type: toTitleCase(listing?.room_type),
    gender: mapGenderPreference(listing?.gender_preference),
    district: listing?.neighbourhood?.name || listing?.district || listing?.address || "Location not specified",
    city: listing?.city?.name || listing?.city_name || "",
    nationality: Array.isArray(listing?.community_preferences_data) ? listing.community_preferences_data.map(item => item?.name).filter(Boolean) : [],
    price: Number(listing?.rent || 0),
    houseType: toTitleCase(listing?.house_type),
    furnished: mapFurnished(listing?.furnished),
    address: listing?.address || "",
    posted_ago: listing?.posted_ago || "",
    isShortlisted: isFavourite,
    is_favourite: isFavourite,
    isOwner: Boolean(listing?.is_owner),
    raw: {
      ...(listing || {}),
      is_favourite: isFavourite
    }
  };
}
function matchesListingSearch(item, searchValue) {
  const normalizedSearch = normalizeSearchText(searchValue);
  if (!normalizedSearch) return true;
  const searchTokens = normalizedSearch.split(" ").filter(Boolean);
  const searchableFields = [normalizeSearchText(item?.title), normalizeSearchText(item?.district), ...(Array.isArray(item?.nationality) ? item.nationality.map(value => normalizeSearchText(value)) : [])].filter(Boolean);
  return searchTokens.every(token => searchableFields.some(fieldValue => fieldValue.includes(token)));
}
export default function ListingsFeed() {
  useLanguage();
  useDocumentTitle("Room Listings");
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedNationality, setSelectedNationality] = useState("");
  const [selectedHouseType, setSelectedHouseType] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState([]);
  const [selectedFurnished, setSelectedFurnished] = useState("");
  const [selectedAvailability, setSelectedAvailability] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [minRent, setMinRent] = useState("");
  const [maxRent, setMaxRent] = useState("");
  const [minRentError, setMinRentError] = useState("");
  const [maxRentError, setMaxRentError] = useState("");
  const [allListings, setAllListings] = useState([]);
  const [isListingsLoading, setIsListingsLoading] = useState(true);
  const [listingsError, setListingsError] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [nationalityOptions, setNationalityOptions] = useState([]);
  const [houseTypeOptions, setHouseTypeOptions] = useState([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(true);
  const [isDistrictLoading, setIsDistrictLoading] = useState(false);
  const [isHouseTypesLoading, setIsHouseTypesLoading] = useState(true);
  const [isCommunityPreferencesLoading, setIsCommunityPreferencesLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(LISTINGS_PER_LOAD);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const observerRef = useRef(null);
  const listingsRequestId = useRef(0);
  const loadMoreTriggerRef = useRef(null);
  const navigate = useNavigate();
  const {
    user: authUser,
    isAuthenticated
  } = useAuth();
  const user = useMemo(() => normalizeUser(authUser || {}), [authUser]);
  const {
    detectedCityId,
    detectedCity,
    isDetectingLocation,
    locationError,
    detectLocation
  } = useLocation();
  const isProtectedShell = Boolean(isAuthenticated);
  const activeCityId = selectedCity || detectedCityId || "";
  const resolveCityNameFromId = useCallback(cityId => {
    if (!cityId || !Array.isArray(cityOptions)) return "";
    const matchedCity = cityOptions.find(item => String(item?.id) === String(cityId));
    return matchedCity?.name || "";
  }, [cityOptions]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_DELAY);
    return () => {
      clearTimeout(timer);
    };
  }, [search]);
  useEffect(() => {
    let isMounted = true;
    const loadCities = async () => {
      try {
        setIsCitiesLoading(true);
        const response = await getCities();
        const isSuccess = response?.is_active === true || response?.status === true;
        const citiesArray = Array.isArray(response?.data) ? response.data : [];
        const mappedCities = isSuccess ? citiesArray.filter(city => city?.id && city?.name).map(city => ({
          id: city.id,
          name: city.name,
          name_ar: city.name_ar || ""
        })) : [];
        if (isMounted) {
          setCityOptions(mappedCities);
        }
      } catch (error) {
        if (isMounted) {
          setCityOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsCitiesLoading(false);
        }
      }
    };
    loadCities();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadPropertyTypes = async () => {
      try {
        setIsHouseTypesLoading(true);
        const response = await getPropertyTypes();
        const isSuccess = response?.is_active === true || response?.status === true;
        const propertyTypesArray = Array.isArray(response?.data) ? response.data : [];
        const mappedPropertyTypes = isSuccess ? propertyTypesArray.filter(item => item?.name).map(item => item.name) : [];
        if (isMounted) {
          setHouseTypeOptions(mappedPropertyTypes);
        }
      } catch (error) {
        if (isMounted) {
          setHouseTypeOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsHouseTypesLoading(false);
        }
      }
    };
    loadPropertyTypes();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadCommunityPreferences = async () => {
      try {
        setIsCommunityPreferencesLoading(true);
        const response = await getCommunityPreferences();
        const isSuccess = response?.is_active === true || response?.status === true;
        const communityArray = Array.isArray(response?.data) ? response.data : [];
        const mappedNationalities = isSuccess ? communityArray.filter(item => item?.name).map(item => item.name) : [];
        if (isMounted) {
          setNationalityOptions(mappedNationalities);
        }
      } catch (error) {
        if (isMounted) {
          setNationalityOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsCommunityPreferencesLoading(false);
        }
      }
    };
    loadCommunityPreferences();
    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    let isMounted = true;
    const loadDistricts = async () => {
      if (!activeCityId) {
        setDistrictOptions([]);
        setSelectedDistrict("");
        return;
      }
      try {
        setIsDistrictLoading(true);
        const response = await getNeighbourhoods(activeCityId);
        const isSuccess = response?.is_active === true || response?.status === true;
        const neighbourhoodsArray = Array.isArray(response?.data) ? response.data : [];
        const mappedDistricts = isSuccess ? neighbourhoodsArray.filter(item => item?.id && item?.name && String(item?.city_id) === String(activeCityId)).map(item => item.name) : [];
        if (isMounted) {
          setDistrictOptions(mappedDistricts);
        }
      } catch (error) {
        if (isMounted) {
          setDistrictOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsDistrictLoading(false);
        }
      }
    };
    loadDistricts();
    return () => {
      isMounted = false;
    };
  }, [activeCityId]);
  const fetchListings = useCallback(async () => {
    const requestId = ++listingsRequestId.current;
    try {
      setIsListingsLoading(true);
      setListingsError("");
      const activeCityName = resolveCityNameFromId(activeCityId);
      const params = {};
      if (activeCityId) {
        params.city_id = Number(activeCityId);
      }
      if (activeCityName) {
        params.city = activeCityName;
      }
      if (minRent && !minRentError) {
        params.min_rent = Number(minRent);
      }
      if (maxRent && !maxRentError) {
        params.max_rent = Number(maxRent);
      }
      if (selectedGender) {
        params.gender_preference = selectedGender === "Male Only" ? "male" : "female";
      }
      if (selectedNationality) {
        params.community_preferences = selectedNationality;
      }
      const [listingsResponse, favouritesResponse] = await Promise.all([getRoomListings(params), isAuthenticated ? getFavouriteRoomListings() : Promise.resolve(null)]);
      if (requestId !== listingsRequestId.current) return;
      const listingsArray = extractListingsArray(listingsResponse);
      const favouriteIds = isAuthenticated ? extractFavouriteIds(favouritesResponse) : new Set();
      const mappedListings = listingsArray.map(item => mapApiListingToCard(item, favouriteIds));
      setAllListings(mappedListings);
    } catch (error) {
      if (requestId !== listingsRequestId.current) return;
      setAllListings([]);
      setListingsError(error?.response?.data?.message || error?.message || "Failed to load listings.");
    } finally {
      if (requestId === listingsRequestId.current) setIsListingsLoading(false);
    }
  }, [activeCityId, resolveCityNameFromId, minRent, maxRent, minRentError, maxRentError, selectedGender, selectedNationality, isAuthenticated]);
  useEffect(() => {
    fetchListings();
  }, [fetchListings]);
  const handleMinRentChange = value => {
    const digitsOnly = String(value || "").replace(/\D/g, "");
    setMinRent(digitsOnly);
    if (!digitsOnly) {
      setMinRentError("");
      return;
    }
    const numericValue = Number(digitsOnly);
    if (numericValue < 100) {
      setMinRentError("Min Rent must be at least 100 SAR.");
      return;
    }
    if (numericValue > 50000) {
      setMinRentError("Min Rent cannot exceed 50000 SAR.");
      return;
    }
    setMinRentError("");
  };
  const handleMaxRentChange = value => {
    const digitsOnly = String(value || "").replace(/\D/g, "");
    setMaxRent(digitsOnly);
    if (!digitsOnly) {
      setMaxRentError("");
      return;
    }
    const numericValue = Number(digitsOnly);
    if (numericValue < 100) {
      setMaxRentError("Max Rent must be at least 100 SAR.");
      return;
    }
    if (numericValue > 50000) {
      setMaxRentError("Max Rent cannot exceed 50000 SAR.");
      return;
    }
    setMaxRentError("");
  };
  const filteredListings = useMemo(() => {
    return allListings.filter(item => {
      const listingNationalities = Array.isArray(item.nationality) ? item.nationality : item.nationality ? [item.nationality] : [];
      const selectedCityName = selectedCity ? resolveCityNameFromId(selectedCity) : "";
      const normalizedSelectedHouseTypes = Array.isArray(selectedHouseType) ? selectedHouseType : [];
      const normalizedSelectedRoomTypes = Array.isArray(selectedRoomType) ? selectedRoomType : [];
      const matchesSearch = matchesListingSearch(item, debouncedSearch);
      const matchesCity = selectedCityName ? item.city === selectedCityName : true;
      const matchesDistrict = selectedDistrict ? item.district === selectedDistrict : true;
      const matchesNationality = selectedNationality ? listingNationalities.includes(selectedNationality) : true;
      const matchesHouseType = normalizedSelectedHouseTypes.length ? normalizedSelectedHouseTypes.includes(item.houseType) : true;
      const matchesRoomType = normalizedSelectedRoomTypes.length ? normalizedSelectedRoomTypes.includes(item.type) : true;
      const matchesFurnished = selectedFurnished ? item.furnished === selectedFurnished : true;
      const matchesGender = selectedGender ? item.gender === selectedGender : true;
      const matchesMinRent = minRent && !minRentError ? item.price >= Number(minRent) : true;
      const matchesMaxRent = maxRent && !maxRentError ? item.price <= Number(maxRent) : true;
      const matchesAvailability = !selectedAvailability || item.raw?.availability_type === selectedAvailability;
      return matchesAvailability && matchesSearch && matchesCity && matchesDistrict && matchesNationality && matchesHouseType && matchesRoomType && matchesFurnished && matchesGender && matchesMinRent && matchesMaxRent;
    });
  }, [allListings, debouncedSearch, selectedCity, selectedDistrict, selectedNationality, selectedHouseType, selectedRoomType, selectedFurnished, selectedAvailability, selectedGender, minRent, maxRent, minRentError, maxRentError, resolveCityNameFromId]);
  const visibleListings = useMemo(() => {
    return filteredListings.slice(0, visibleCount);
  }, [filteredListings, visibleCount]);
  const hasMoreListings = visibleCount < filteredListings.length;
  const loadMoreListings = useCallback(() => {
    if (!hasMoreListings || isFetchingMore) return;
    setIsFetchingMore(true);
    setTimeout(() => {
      setVisibleCount(prev => prev + LISTINGS_PER_LOAD);
      setIsFetchingMore(false);
    }, 400);
  }, [hasMoreListings, isFetchingMore]);
  useEffect(() => {
    setVisibleCount(LISTINGS_PER_LOAD);
  }, [debouncedSearch, selectedCity, selectedDistrict, selectedNationality, selectedHouseType, selectedRoomType, selectedFurnished, selectedAvailability, selectedGender, minRent, maxRent, allListings]);
  useEffect(() => {
    const triggerElement = loadMoreTriggerRef.current;
    if (!triggerElement) return;
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    observerRef.current = new IntersectionObserver(entries => {
      const firstEntry = entries[0];
      if (firstEntry?.isIntersecting && hasMoreListings && !isFetchingMore) {
        loadMoreListings();
      }
    }, {
      root: null,
      rootMargin: "300px 0px",
      threshold: 0.01
    });
    observerRef.current.observe(triggerElement);
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMoreListings, hasMoreListings, isFetchingMore, visibleListings.length]);
  useEffect(() => {
    const handleScrollFallback = () => {
      if (!hasMoreListings || isFetchingMore) return;
      const triggerElement = loadMoreTriggerRef.current;
      if (!triggerElement) return;
      const rect = triggerElement.getBoundingClientRect();
      const isNearViewportBottom = rect.top <= window.innerHeight + 300;
      if (isNearViewportBottom) {
        loadMoreListings();
      }
    };
    handleScrollFallback();
    window.addEventListener("scroll", handleScrollFallback, {
      passive: true
    });
    window.addEventListener("resize", handleScrollFallback);
    return () => {
      window.removeEventListener("scroll", handleScrollFallback);
      window.removeEventListener("resize", handleScrollFallback);
    };
  }, [loadMoreListings, hasMoreListings, isFetchingMore, visibleListings.length]);
  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setSelectedCity("");
    setSelectedDistrict("");
    setSelectedNationality("");
    setSelectedHouseType([]);
    setSelectedRoomType([]);
    setSelectedFurnished("");
    setSelectedAvailability("");
    setSelectedGender("");
    setMinRent("");
    setMaxRent("");
    setMinRentError("");
    setMaxRentError("");
    setVisibleCount(LISTINGS_PER_LOAD);
  };
  const resultsHeadingCity = selectedCity ? resolveCityNameFromId(selectedCity) : detectedCity;
  const handleRetryLocation = async () => {
    try {
      await detectLocation(cityOptions);
    } catch (error) {

      // handled by context
    }
  };
  const handleListingShortlistChange = useCallback((nextIsShortlisted, updatedListing) => {
    if (!updatedListing?.id) return;
    setAllListings(prev => prev.map(item => String(item.id) === String(updatedListing.id) ? {
      ...item,
      ...updatedListing,
      isShortlisted: nextIsShortlisted,
      is_favourite: nextIsShortlisted,
      raw: {
        ...(item.raw || {}),
        ...(updatedListing.raw || {}),
        is_favourite: nextIsShortlisted
      }
    } : item));
  }, []);
  const pageContent = <main className="bb-listings-feed-main">
      <Container fluid={isProtectedShell}>
        <div className="bb-listings-feed-hero">
          <div>
            <h1 className="bb-listings-feed-hero__title">{t("Room Listings")}</h1>
            <p className="bb-listings-feed-hero__subtitle">
              {t(resultsHeadingCity ? `Showing room discovery for ${resultsHeadingCity}.` : "Discover room options across cities and explore spaces that match your needs, location preferences, and budget.")}
            </p>
          </div>

          {isProtectedShell ? <Button className="bb-my-posts-hero__btn btn btn-primary" onClick={() => navigate(ROUTES.CREATE_LISTING)}>{t("Post a Room")}


        </Button> : null}
        </div>

        <ListingFilters selectedAvailability={selectedAvailability} onAvailabilityChange={setSelectedAvailability} search={search} selectedCity={selectedCity ? resolveCityNameFromId(selectedCity) : ""} selectedDistrict={selectedDistrict} selectedNationality={selectedNationality} selectedHouseType={selectedHouseType} selectedRoomType={selectedRoomType} selectedFurnished={selectedFurnished} selectedGender={selectedGender} minRent={minRent} maxRent={maxRent} cityOptions={cityOptions.map(item => item.name)} districtOptions={districtOptions} nationalityOptions={nationalityOptions} houseTypeOptions={houseTypeOptions} roomTypeOptions={roomTypeOptions} furnishedOptions={furnishedOptions} genderOptions={genderOptions} showAdvanced={showAdvanced} onToggleAdvanced={() => setShowAdvanced(prev => !prev)} onSearchChange={setSearch} onCityChange={cityName => {
        const matchedCity = cityOptions.find(item => item.name === cityName);
        setSelectedCity(matchedCity?.id ? String(matchedCity.id) : "");
        setSelectedDistrict("");
      }} onDistrictChange={setSelectedDistrict} onNationalityChange={setSelectedNationality} onHouseTypeChange={setSelectedHouseType} onRoomTypeChange={setSelectedRoomType} onFurnishedChange={setSelectedFurnished} onGenderChange={setSelectedGender} onMinRentChange={handleMinRentChange} onMaxRentChange={handleMaxRentChange} onReset={resetFilters} minRentError={minRentError} maxRentError={maxRentError} />


        {!activeCityId && !isDetectingLocation ? <div className="bb-listings-feed__results">
            <p className="bb-listings-feed__results-text">
              {t(locationError ? "Location unavailable. Showing all listings instead." : "Showing all listings.")}{t(" ")}
              <button type="button" className="bb-listings-feed__retry-location-btn" onClick={handleRetryLocation}>{t("Use My Location")}


          </button>
            </p>
          </div> : null}

        {isDetectingLocation && !activeCityId ? <div className="bb-listings-feed__loader bb-listings-feed__loader--initial">
            <Spinner animation="border" size="sm" />
            <span>{t("Detecting your city...")}</span>
          </div> : isListingsLoading || isCitiesLoading || isDistrictLoading || isHouseTypesLoading || isCommunityPreferencesLoading ? <div className="bb-listings-feed__loader bb-listings-feed__loader--initial">
            <Spinner animation="border" size="sm" />
            <span>{t("Loading listings...")}</span>
          </div> : listingsError ? <EmptyState title={t("Unable to load listings")} description={t(listingsError)} actionLabel={t("Try Again")} onActionClick={fetchListings} /> : <>
            <div className="bb-listings-feed__results">
              <p className="bb-listings-feed__results-text">{t("Showing {0} of {1}", {
              0: visibleListings.length,
              1: formatCount(filteredListings.length, "listing")
            })}
                {t(resultsHeadingCity ? ` in ${resultsHeadingCity}` : "")}
              </p>
            </div>

            {filteredListings.length ? <>
                <Row className="g-4">
                  {visibleListings.map(listing => <Col key={listing.id} xl={4} md={6}>
                      <ListingCard listing={listing} onShortlistChange={handleListingShortlistChange} />

                    </Col>)}
                </Row>

                {hasMoreListings ? <div ref={loadMoreTriggerRef} className="bb-listings-feed__load-more-trigger">

                    {isFetchingMore ? <div className="bb-listings-feed__loader">
                        <Spinner animation="border" size="sm" />
                        <span>{t("Loading more listings...")}</span>
                      </div> : <div className="bb-listings-feed__loader-placeholder">{t("Scroll to load more listings")}

            </div>}
                  </div> : filteredListings.length > LISTINGS_PER_LOAD ? <div className="bb-listings-feed__end-message">{t("You have reached the end of the listings.")}

          </div> : null}
              </> : <EmptyState title={t("No listings found")} description={t(resultsHeadingCity ? `No room listings are available in ${resultsHeadingCity} for the selected filters yet.` : "Try adjusting your search or advanced filters to discover more room listings.")} actionLabel={t("Reset Filters")} onActionClick={resetFilters} actionClassName="bb-listings-feed__reset-filters-btn" />}
          </>}
      </Container>
    </main>;
  if (!isProtectedShell) {
    return <div className="bb-public-listings-page">
        <header className="bb-public-listings-topbar">
          <Container>
            <div className="bb-public-listings-topbar__inner">
              <div className="bb-public-listings-topbar__logo" onClick={() => navigate(ROUTES.ROOT)} role="button" tabIndex={0} onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                navigate(ROUTES.ROOT);
              }
            }}>

                <Logo />
              </div>

              <div className="bb-public-listings-topbar__actions">
                <Button variant="outline-primary" className="bb-brand-btn-outline" onClick={() => navigate(ROUTES.ROOT)}>{t("Back to Home")}


                </Button>
                <Button className="bb-login-register-btn" onClick={() => navigate(ROUTES.LOGIN)}>{t("Login / Register")}


                </Button>
              </div>
            </div>
          </Container>
        </header>

        {t(pageContent)}
      </div>;
  }
  return <div className="bb-dashboard-layout">
      <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
        <Sidebar user={user} />
      </div>

      <div className="bb-dashboard-layout__content">
        <Header onMenuClick={() => setShowMobileNav(true)} />

        <VerificationNoticeBanner user={user} />

        <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={user} />


        {t(pageContent)}

        <Footer />
      </div>
    </div>;
}
